/// <amd-module name="Wiki/ViewCommon/Utils/WikiHelper" />
import { equals, format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureEnabled, isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssHistoryService, StateMergeOptions } from "VSS/Platform/History";
import { IVssLocationService } from "VSS/Platform/Location";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { combineUrlPaths, parseRouteTemplate, routeUrl } from "VSS/Platform/Util/Url";

import { ITfsNavigationService } from "Tfs/Platform/Navigation";
import { ITfsPageService } from "Tfs/Platform/Page";
import { WikiType, WikiV2 } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { ActionIds, SpecialChars, UrlConstants, UrlEscapeConstants } from "Wiki/ViewCommon/Constants";
import { UrlParameters } from "Wiki/ViewCommon/Contracts";
import { Areas, TelemetryConstants } from "Wiki/ViewCommon/CustomerIntelligenceConstants";
import * as Resources from "Wiki/ViewCommon/Resources";
import { getFileName, getFriendlyPageTitle, normalizeWikiPagePath } from "Wiki/ViewCommon/Utils/PathHelper";

const simpleUrlRouteId = "ms.vss-wiki-web.wiki-overview-nwp-route2";

export function isCodeWiki(wiki: WikiV2): boolean {
    return wiki.type === WikiType.CodeWiki;
}

export function isEdge(): boolean {
    const edge = /edge\/([\d+.]+)/i.exec(navigator.userAgent);
    return !!edge;
}

export function isFirefox(): boolean {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const mozilla = /mozilla.*rv:([\d+.]+)/i.exec(navigator.userAgent);

    return !!mozilla && userAgent.indexOf("trident") === -1;
}

export function isIE(): boolean {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const msie = /msie ([\d+.]+)/i.exec(navigator.userAgent);

    return !!msie || userAgent.indexOf("trident") !== -1;
}

export function isSafari(): boolean {
    const safari = /Safari\/([\d.]+)/i.exec(window.navigator.userAgent);
    return !!safari && navigator.userAgent.toLowerCase().indexOf("chrome") === -1;
}

export function getVersionFromBranchName(branchName: string): string {
    return "GB" + branchName;
}

export function updateUrl(
    pageContext: IVssPageContext,
    urlParameters: UrlParameters,
    stateParameters: { wikiIdentifier?: string; wikiVersion?: string } = {},
    mergeOption: StateMergeOptions = StateMergeOptions.currentState,
    replaceState?: boolean
): void {
    const fullState = getNewWikiState(pageContext, urlParameters, stateParameters, mergeOption);

    setWikiWindowTitle(pageContext, fullState);

    // If the URL contains pageId, we prefer new route template
    // note: we support simple URL only in case of view url
    if (
        isSimpleUrlEnabled(pageContext) &&
        fullState[UrlConstants.PageIdParam] &&
        (!urlParameters._a || equals(urlParameters._a, ActionIds.View, true))
    ) {
        _updateToSimpleUrl(pageContext, fullState, replaceState);
    } else {
        const historyService = pageContext.getService<IVssHistoryService>("IVssHistoryService");
        if (replaceState) {
            historyService.replaceState(fullState);
        } else {
            historyService.pushState(fullState);
        }
    }
}

export function updateToPagePathBasedUrlIfNeeded(
    pageContext: IVssPageContext,
    urlParameters: UrlParameters,
    stateParameters: { wikiIdentifier?: string; wikiVersion?: string } = {},
    replaceState?: boolean
): void {
    const newUrlState = getPagePathBasedUrlAndState(pageContext, urlParameters, stateParameters);

    const historyService = pageContext.getService<IVssHistoryService>("IVssHistoryService");

    // Note: we need to set the state even though we are setting url,
    // as it might be needed during subsequent route value merges
    if (replaceState) {
        historyService.replaceState(newUrlState.state, newUrlState.url);
    } else {
        historyService.pushState(newUrlState.state, newUrlState.url);
    }
}

export function getPagePathBasedUrlAndState(
    pageContext: IVssPageContext,
    urlParameters: UrlParameters,
    stateParameters: { wikiIdentifier?: string; wikiVersion?: string } = {}
): { url: string; state: { [key: string]: string } } {
    const tfsPageService = pageContext.getService<ITfsPageService>("ITfsPageService");

    const defaultParams: { [key: string]: string } = {};
    const data = tfsPageService.getData();
    if (data && data.project) {
        defaultParams[UrlConstants.ProjectParam] = data.project.name;
    }

    const fullState = getNewWikiState(pageContext, { ...stateParameters, ...urlParameters, ...defaultParams }, {}, StateMergeOptions.none);

    // Since we want path based URL even if pageid is present and route templates has a pageid based route
    // we need to force the usage of this route template
    // this is needed for edit, revision history usecases
    const wikiOverviewRouteTemplate = "{project}/_wiki/wikis/{*wikiIdentifier}";
    const newUrl = routeUrlFromRouteTemplate(pageContext, wikiOverviewRouteTemplate, fullState);
    return { url: newUrl, state: fullState };
}

export function routeUrlFromRouteTemplate(pageContext: IVssPageContext, routeTemplate: string, routeValues: { [key: string]: string }): string {
    const parsedRouteTemplate = parseRouteTemplate(routeTemplate);
    const relativeUrl = routeUrl([parsedRouteTemplate], routeValues);

    // Add the host path
    let hostPath = "";
    const pageData = pageContext.getService<IVssPageService>("IVssPageService").getData();
    if (pageData && pageData.hostPath) {
        hostPath = pageData.hostPath;
    }

    return hostPath ? combineUrlPaths(encodeURI(hostPath), relativeUrl) : relativeUrl;
}

export function setWikiWindowTitle(pageContext: IVssPageContext, urlParameters: UrlParameters): void {
    const normalizedPagePath = normalizeWikiPagePath(urlParameters.pagePath);
    const pageTitle = getFileName(normalizedPagePath);

    let windowTitle = pageTitle;
    if (pageTitle && urlParameters._a && equals(urlParameters._a, ActionIds.Edit, true)) {
        windowTitle = format(Resources.WindowTitleWhileEditing, pageTitle);
    }
    windowTitle = windowTitle || Resources.WindowTitleDefault;
    setWindowTitle(pageContext, windowTitle);
}

export function setWindowTitle(pageContext: IVssPageContext, windowTitle: string): void {
    const navigationService = pageContext.getService<ITfsNavigationService>("ITfsNavigationService");
    navigationService.setPageTitle(windowTitle);
}

export function getWikiTypeString(wikiType: WikiType): string {
    if (wikiType === WikiType.ProjectWiki) {
        return TelemetryConstants.WikiTypeProject;
    }

    return TelemetryConstants.WikiTypeCode;
}

export function publish(pageContext: IVssPageContext, feature: string, wikiType?: WikiType | string, properties?: { [key: string]: any }): void {
    const service = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    const tfsPageData = pageContext.getService<ITfsPageService>("ITfsPageService").getData();
    const projectId = tfsPageData && tfsPageData.project && tfsPageData.project.id;
    let wikiTypeString: string | undefined;
    if (wikiType) {
        wikiTypeString = typeof wikiType === "string" ? wikiType : getWikiTypeString(wikiType);
    }
    service.publishEvent(Areas.Wiki, feature, {
        ...properties,
        wikiType: wikiTypeString,
        [TelemetryConstants.NewWiki]: true,
        projectId
    });
}

export function navigateToUrl(
    pageContext: IVssPageContext,
    projectId: string,
    wikiName: string | undefined,
    routeValues?: { [key: string]: string }
): void {
    const navigationService = pageContext.getService<IVssNavigationService>("IVssNavigationService");
    const routeId = navigationService.getCurrentRoute().routeId;
    if (!routeValues) {
        routeValues = {};
    }

    routeValues[UrlConstants.ProjectParam] = projectId;
    if (!!wikiName) {
        routeValues[UrlConstants.WikiIdentifierParam] = wikiName;
    }

    const newUrl = pageContext.getService<IVssLocationService>("IVssLocationService").routeUrl(routeId, routeValues);
    onClickFPS(pageContext, newUrl, true);
}

// Should be called from Wiki hub only
export function getWikiUrl(pageContext: IVssPageContext, urlParameters: UrlParameters, mergeOption?: StateMergeOptions): string {
    const fullState = getNewWikiState(pageContext, urlParameters, {}, mergeOption);
    const historyService = pageContext.getService<IVssHistoryService>("IVssHistoryService");
    return historyService.generateUrl(fullState, mergeOption);
}

// Should be called from Wiki hub only
export function getWikiHomePageUrl(pageContext: IVssPageContext, projectId: string): string {
    const navigationService = pageContext.getService<IVssNavigationService>("IVssNavigationService");
    const routeId = navigationService.getCurrentRoute().routeId;
    const wikiHomePageUrl = pageContext
        .getService<IVssLocationService>("IVssLocationService")
        .routeUrl(routeId, { [UrlConstants.ProjectParam]: projectId });
    return wikiHomePageUrl;
}

/**
 * returns inout order + 1, bounded to maximum order possible
 * @param order input order
 */
export function boundedOrderIncrement(order: number): number {
    // This is C# int max
    const orderMaxValue = 2147483647;
    return order < orderMaxValue ? order + 1 : orderMaxValue;
}

export function isCodeEditEnabled(pageContext: IVssPageContext): boolean {
    return isFeatureFlagEnabled(pageContext, "WebAccess.NewWiki.RichCodeWikiEditing");
}

export function isIncludeEditingAllowed(pageContext: IVssPageContext): boolean {
    return isFeatureFlagEnabled(pageContext, "Wiki.EnableTemplateEditing") && isFeatureEnabled(pageContext, "ms.vss-wiki-web.wiki-templates-editing");
}

export function isSimpleUrlEnabled(pageContext: IVssPageContext): boolean {
    return isFeatureFlagEnabled(pageContext, "Wiki.SimpleUrl");
}

export function getNewWikiState(
    pageContext: IVssPageContext,
    urlParameters: UrlParameters,
    stateParameters: { wikiIdentifier?: string; wikiVersion?: string } = {},
    mergeOption: StateMergeOptions = StateMergeOptions.currentState
): { [key: string]: string } {
    let fullState: { [key: string]: string } = {};
    const historyService = pageContext.getService<IVssHistoryService>("IVssHistoryService");
    const currentState = historyService.getState();
    const currentRouteValues = historyService.getCurrentRouteValues();

    const newUrlParameters: UrlParameters = { ...urlParameters };
    /* '-' in URL relates to space in the page path.s
     * So we need to convert '-' in page path to '%2D' to differentiate.
     */
    if (newUrlParameters.pagePath) {
        newUrlParameters.pagePath = newUrlParameters.pagePath.replace(UrlEscapeConstants.HyphenRegExp, UrlEscapeConstants.HyphenEncoding);
    }

    // Show action in lower case. Also, we don't want to show action = view in the url
    if (newUrlParameters._a) {
        if (!equals(newUrlParameters._a, ActionIds.View, true)) {
            newUrlParameters._a = newUrlParameters._a.toLowerCase();
        } else {
            newUrlParameters._a = undefined;
        }
    }

    // We always need to have wikiVersion in the Url
    if (!newUrlParameters.wikiVersion && currentState[UrlConstants.WikiVersionParam]) {
        newUrlParameters.wikiVersion = currentState[UrlConstants.WikiVersionParam];
    }

    if (mergeOption === StateMergeOptions.none) {
        fullState = { ...stateParameters, ...(newUrlParameters as any) };
    } else if (mergeOption === StateMergeOptions.currentState) {
        fullState = { ...currentState, ...stateParameters, ...(newUrlParameters as any) };
    } else if (mergeOption === StateMergeOptions.routeValues) {
        if (
            !currentRouteValues[UrlConstants.WikiIdentifierParam] &&
            !stateParameters.wikiIdentifier &&
            currentState[UrlConstants.WikiIdentifierParam]
        ) {
            stateParameters.wikiIdentifier = currentState[UrlConstants.WikiIdentifierParam];
        }

        fullState = { ...currentRouteValues, ...stateParameters, ...(newUrlParameters as any) };
    }

    return fullState;
}

/**
 * Returns simplified URL for the route values mentioned
 * @param pageContext
 * @param routeValues
 */
export function getSimpleUrl(pageContext: IVssPageContext, routeValues: { [key: string]: string }): string {
    const locationService = pageContext.getService<IVssLocationService>("IVssLocationService");
    return locationService.routeUrl(simpleUrlRouteId, routeValues);
}

function _updateToSimpleUrl(pageContext: IVssPageContext, fullState: { [key: string]: string }, replaceState?: boolean): void {
    const wikiUrlParamkeys: string[] = Object.keys(UrlConstants);
    const routeValues: { [key: string]: string } = {};

    wikiUrlParamkeys.forEach(key => {
        // To keep Urls simple, skip page path and version.
        const param = UrlConstants[key];
        if (param !== UrlConstants.PagePathParam && param !== UrlConstants.WikiVersionParam && fullState[param]) {
            routeValues[param] = fullState[param];
        }
    });

    let pagePath = fullState[UrlConstants.PagePathParam];
    if (pagePath) {
        // since we previously used to encode hypen in pagepath as it was in URL
        // we need to undo it
        pagePath = pagePath.replace(UrlEscapeConstants.HyphenEncodingRegExp, SpecialChars.Hyphen);
        routeValues[UrlConstants.FriendlyNameParam] = getFriendlyPageTitle(pagePath);
    }

    const simpleUrl = getSimpleUrl(pageContext, routeValues);

    // This is used in history back navigation, so save these query param as they are no longer present in Url
    const additionalData = {
        [UrlConstants.PagePathParam]: pagePath,
        [UrlConstants.WikiVersionParam]: fullState[UrlConstants.WikiVersionParam]
    };

    const historyService = pageContext.getService<IVssHistoryService>("IVssHistoryService");
    if (replaceState) {
        historyService.replaceState(routeValues, simpleUrl, simpleUrlRouteId, additionalData);
    } else {
        historyService.pushState(routeValues, simpleUrl, simpleUrlRouteId, additionalData);
    }
}
